<template>
  <div class="device-management">
    <div class="page-header">
      <h2>设备管理</h2>
      <el-button 
        v-if="isAdmin"
        type="primary" 
        @click="showAddDialog = true"
      >
        <el-icon><Plus /></el-icon>
        添加设备
      </el-button>
    </div>
    
    <!-- 搜索栏 -->
    <div class="search-bar">
      <div style="display: flex; gap: 10px; align-items: center;">
        <el-input
          v-model="searchText"
          placeholder="搜索设备名称或编号"
          style="width: 300px;"
          clearable
        >
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
        </el-input>
        <el-button @click="performSearch">搜索</el-button>
      </div>
      
      <div style="display: flex; gap: 10px;" v-if="isAdmin">
        <el-button type="success" @click="downloadTemplate">
          <el-icon><Download /></el-icon>
          下载模板
        </el-button>
        <el-upload
          ref="uploadRef"
          :action="uploadUrl"
          :headers="uploadHeaders"
          :on-success="handleUploadSuccess"
          :on-error="handleUploadError"
          :before-upload="beforeUpload"
          :show-file-list="false"
          accept=".xlsx,.xls"
        >
          <el-button type="warning">
            <el-icon><Upload /></el-icon>
            Excel导入
          </el-button>
        </el-upload>
      </div>
    </div>
    
    <!-- 设备列表 -->
    <div class="device-list">
      <el-table 
        :data="filteredDevices" 
        style="width: 100%;" 
        border
        stripe
        :header-cell-style="{background:'#f5f7fa',color:'#606266'}"
        v-loading="loading"
        row-key="id"
        :row-class-name="getRowClassName"
      >
        <el-table-column label="序号" width="80" align="center">
          <template #default="{ $index }">
            <span style="font-weight: bold; color: #409EFF;">{{ currentPage * pageSize + $index + 1 }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="deviceNumber" label="设备编号" width="120" sortable />
        <el-table-column prop="deviceName" label="设备名称" width="150" sortable />
        <el-table-column prop="deviceType" label="设备类型" width="120" sortable />
        <el-table-column prop="location" label="存放位置" width="120" sortable />
        <el-table-column prop="model" label="规格型号" width="150" sortable />
        <el-table-column prop="status" label="状态" width="100" sortable>
          <template #default="scope">
            <el-tag :type="getStatusType(scope.row.status)">{{ getStatusText(scope.row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="description" label="描述" min-width="150" />
        <el-table-column prop="createdAt" label="添加日期" width="120" sortable>
          <template #default="scope">
            {{ formatDate(scope.row.createdAt) }}
          </template>
        </el-table-column>
        
        <el-table-column label="操作" width="200" fixed="right" v-if="isAdmin">
          <template #default="scope">
            <el-button type="primary" size="small" @click="editDevice(scope.row)">编辑</el-button>
            <el-button type="success" size="small" @click="generateQRCode(scope.row)">二维码</el-button>
            <el-button type="danger" size="small" @click="deleteDevice(scope.row.id)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>
    
    <!-- 分页 -->
    <div style="display:flex; justify-content:flex-end; margin-top: 16px;">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="devicesTotal"
        :page-size="pageSize"
        :current-page="currentPage + 1"
        :page-sizes="[10, 15, 20, 30, 50]"
        @current-change="onPageChange"
        @size-change="onSizeChange"
      />
    </div>
    
    <!-- 添加/编辑设备对话框 -->
    <el-dialog 
      :title="editingDevice ? '编辑设备' : '添加设备'"
      v-model="showAddDialog"
      width="500px"
    >
      <el-form 
        ref="deviceForm" 
        :model="deviceForm" 
        :rules="deviceRules"
        label-width="100px"
      >
        <el-form-item label="设备编号" prop="deviceNumber">
          <el-input v-model="deviceForm.deviceNumber" placeholder="请输入设备编号" />
        </el-form-item>
        <el-form-item label="设备名称" prop="deviceName">
          <el-input v-model="deviceForm.deviceName" placeholder="请输入设备名称" />
        </el-form-item>
        <el-form-item label="设备类型" prop="deviceType">
          <el-select v-model="deviceForm.deviceType" placeholder="请选择设备类型">
            <el-option label="电脑" value="computer" />
            <el-option label="打印机" value="printer" />
            <el-option label="投影仪" value="projector" />
            <el-option label="GPS" value="gps" />
            <el-option label="水准仪" value="level" />
            <el-option label="对讲机" value="walkie_talkie" />
            <el-option label="测深仪" value="depth_sounder" />
            <el-option label="全站仪" value="total_station" />
            <el-option label="激光扫描仪" value="laser_scanner" />
            <el-option label="测距仪" value="rangefinder" />
            <el-option label="无人测量船" value="unmanned_survey_vessel" />
            <el-option label="无人机" value="drone" />
            <el-option label="其他" value="other" />
          </el-select>
        </el-form-item>
        <el-form-item label="存放位置" prop="location">
          <el-input v-model="deviceForm.location" placeholder="请输入存放位置" />
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input 
            v-model="deviceForm.description" 
            type="textarea" 
            placeholder="请输入设备描述"
            :rows="3"
          />
        </el-form-item>
        <el-form-item label="送审日期" prop="reviewDate">
          <el-date-picker
            v-model="deviceForm.reviewDate"
            type="date"
            placeholder="请选择送审日期"
            format="YYYY/MM/DD"
            value-format="YYYY-MM-DD"
            style="width: 100%;"
          />
        </el-form-item>
      </el-form>
      
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showAddDialog = false">取消</el-button>
          <el-button type="primary" @click="saveDevice" :loading="saving">
            {{ editingDevice ? '更新' : '添加' }}
          </el-button>
        </span>
      </template>
    </el-dialog>
    
    <!-- 二维码对话框 -->
    <el-dialog 
      title="设备二维码"
      v-model="showQRDialog"
      width="400px"
    >
      <div class="qr-container">
        <div ref="qrcode" class="qr-code"></div>
        <p class="qr-info">{{ selectedDevice?.deviceName }}</p>
        <p class="qr-number">设备编号: {{ selectedDevice?.deviceNumber }}</p>
      </div>
      
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showQRDialog = false">关闭</el-button>
          <el-button type="primary" @click="downloadQRCode">下载二维码</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Search, Download, Upload } from '@element-plus/icons-vue'
import { mapGetters } from 'vuex'
import QRCode from 'qrcode'

export default {
  name: 'DeviceManagement',
  components: {
    Plus,
    Search,
    Download,
    Upload
  },
  data() {
    return {
      searchText: '',
      loading: false,
      saving: false,
      showAddDialog: false,
      showQRDialog: false,
      editingDevice: null,
      selectedDevice: null,
      // 新增：分页本地状态（与 store 同步）
      currentPage: 0,
      pageSize: 15,
      deviceForm: {
        deviceNumber: '',
        deviceName: '',
        deviceType: '',
        location: '',
        description: '',
        reviewDate: ''
      },
      deviceRules: {
        deviceNumber: [
          { required: true, message: '请输入设备编号', trigger: 'blur' }
        ],
        deviceName: [
          { required: true, message: '请输入设备名称', trigger: 'blur' }
        ],
        deviceType: [
          { required: true, message: '请选择设备类型', trigger: 'change' }
        ],
        location: [
          { required: true, message: '请输入存放位置', trigger: 'blur' }
        ]
      },
      // Excel导入相关
      uploadUrl: '/api/admin/devices/excel-import',
      uploadHeaders: {
        'Authorization': 'Bearer ' + localStorage.getItem('token')
      }
    }
  },
  computed: {
    ...mapGetters(['isAdmin']),
    filteredDevices() {
      // 改为直接使用服务端返回的当前页数据
      return this.$store.state.devices || []
    },
    devicesTotal() {
      return this.$store.state.devicesTotal || 0
    }
  },
  methods: {
    async fetchDevices() {
      this.loading = true
      try {
        await this.$store.dispatch('fetchDevices', { page: this.currentPage, size: this.pageSize, keyword: this.searchText })
        // 同步 store 分页数据
        this.currentPage = this.$store.state.devicesPage || 0
        this.pageSize = this.$store.state.devicesSize || 15
      } catch (error) {
        ElMessage.error('获取设备列表失败')
      } finally {
        this.loading = false
      }
    },
    onPageChange(page) {
      // Element Plus 分页组件是从 1 开始的，这里转换为后端的 0 基页码
      this.currentPage = page - 1
      this.fetchDevices()
    },
    onSizeChange(size) {
      this.pageSize = size
      // 切换每页数量后回到第一页
      this.currentPage = 0
      this.fetchDevices()
    },
    
    performSearch() {
      // 点击搜索时触发服务端分页检索
      this.currentPage = 0
      this.fetchDevices()
    },
    
    editDevice(device) {
      this.editingDevice = device
      this.deviceForm = { ...device }
      this.showAddDialog = true
    },
    
    async saveDevice() {
      try {
        await this.$refs.deviceForm.validate()
        this.saving = true
        
        // 构造符合后端期望的DeviceRequestDTO格式
        const requestData = {
          device: {
            deviceNumber: this.deviceForm.deviceNumber,
            deviceName: this.deviceForm.deviceName,
            deviceType: this.deviceForm.deviceType,
            location: this.deviceForm.location,
            description: this.deviceForm.description,
            reviewDate: this.deviceForm.reviewDate
          },
          dynamicFields: {} // 空的动态字段
        }
        
        if (this.editingDevice) {
          await this.$store.dispatch('updateDevice', {
            ...requestData,
            id: this.editingDevice.id
          })
          ElMessage.success('设备更新成功')
        } else {
          await this.$store.dispatch('addDevice', requestData)
          ElMessage.success('设备添加成功')
        }
        
        this.showAddDialog = false
        this.resetForm()
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '操作失败')
      } finally {
        this.saving = false
      }
    },
    
    async deleteDevice(device) {
      try {
        await ElMessageBox.confirm(
          `确定要删除设备 "${device.deviceName}" 吗？`,
          '确认删除',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        
        await this.$store.dispatch('deleteDevice', device.id)
        ElMessage.success('设备删除成功')
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('删除失败')
        }
      }
    },
    
    async generateQRCode(device) {
      this.selectedDevice = device
      this.showQRDialog = true
      
      this.$nextTick(async () => {
        try {
          // 清理之前的二维码
          const qrContainer = this.$refs.qrcode
          if (qrContainer) {
            qrContainer.innerHTML = ''
          }
          
          const qrData = JSON.stringify({
            deviceId: device.id,
            deviceNumber: device.deviceNumber,
            deviceName: device.deviceName
          })
          
          // 创建canvas元素
          const canvas = document.createElement('canvas')
          qrContainer.appendChild(canvas)
          
          await QRCode.toCanvas(canvas, qrData, {
            width: 200,
            margin: 2
          })
        } catch (error) {
          ElMessage.error('生成二维码失败: ' + error.message)
        }
      })
    },
    
    async downloadQRCode() {
      try {
        const canvas = this.$refs.qrcode.querySelector('canvas')
        const link = document.createElement('a')
        link.download = `${this.selectedDevice.deviceNumber}_qrcode.png`
        link.href = canvas.toDataURL()
        link.click()
      } catch (error) {
        ElMessage.error('下载失败')
      }
    },
    
    resetForm() {
      this.editingDevice = null
      this.deviceForm = {
        deviceNumber: '',
        deviceName: '',
        deviceType: '',
        location: '',
        description: '',
        reviewDate: ''
      }
      this.$refs.deviceForm?.resetFields()
    },
    
    getStatusText(status) {
      const statusMap = {
        // 处理后端返回的大写枚举值
        'AVAILABLE': '可用',
        'BORROWED': '已借出', 
        'MAINTENANCE': '维护中',
        'DAMAGED': '损坏',
        // 兼容小写值
        'available': '可用',
        'borrowed': '已借出',
        'maintenance': '维护中',
        'damaged': '损坏'
      }
      return statusMap[status] || status
    },
    
    getStatusType(status) {
      const typeMap = {
        // 处理后端返回的大写枚举值
        'AVAILABLE': 'success',
        'BORROWED': 'warning',
        'MAINTENANCE': 'info',
        'DAMAGED': 'danger',
        // 兼容小写值
        'available': 'success',
        'borrowed': 'warning', 
        'maintenance': 'info',
        'damaged': 'danger'
      }
      return typeMap[status] || 'info'
    },
    
    // Excel导入相关方法
    async downloadTemplate() {
      try {
        const token = localStorage.getItem('token')
        const response = await fetch('/api/admin/devices/excel-template', {
          method: 'GET',
          headers: {
            'Authorization': 'Bearer ' + token
          }
        })
        
        if (!response.ok) {
          throw new Error('下载失败')
        }
        
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = '设备信息导入模板.xlsx'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        ElMessage.success('模板下载成功')
      } catch (error) {
        ElMessage.error('模板下载失败：' + error.message)
      }
    },
    
    beforeUpload(file) {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                     file.type === 'application/vnd.ms-excel'
      const isLt10M = file.size / 1024 / 1024 < 10
      
      if (!isExcel) {
        ElMessage.error('只能上传Excel文件！')
        return false
      }
      if (!isLt10M) {
        ElMessage.error('文件大小不能超过10MB！')
        return false
      }
      
      // 更新上传头部的token
      this.uploadHeaders.Authorization = 'Bearer ' + localStorage.getItem('token')
      return true
    },
    
    handleUploadSuccess(response) {
      if (response.success) {
        ElMessage.success(`导入完成！成功导入${response.successCount}条记录，失败${response.errorCount}条`)
        if (response.errorMessages && response.errorMessages.length > 0) {
          console.log('导入错误详情：', response.errorMessages)
          ElMessage.warning('部分记录导入失败，请查看控制台了解详情')
        }
        this.fetchDevices() // 刷新设备列表
      } else {
        ElMessage.error('导入失败：' + response.message)
      }
    },
    
    handleUploadError(error) {
      ElMessage.error('文件上传失败：' + error.message)
    },
    
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN')
    },
    
    // 判断当前日期距离送审日期是否在30天以内
    isWithin30Days(reviewDate) {
      if (!reviewDate) return false
      const today = new Date()
      today.setHours(0, 0, 0, 0) // 设置为当天开始时间
      const review = new Date(reviewDate)
      review.setHours(0, 0, 0, 0) // 设置为当天开始时间
      
      const diffTime = today - review
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      
      // 判断当前日期距离送审日期是否在30天以内
      return diffDays >= 0 && diffDays <= 30
    },
    
    // 获取表格行的CSS类名
    getRowClassName({ row }) {
      if (this.isWithin30Days(row.reviewDate)) {
        return 'review-warning-row'
      }
      return ''
    }
  },
  
  mounted() {
    // 初始化拉取第一页
    this.currentPage = this.$store.state.devicesPage || 0
    this.pageSize = this.$store.state.devicesSize || 15
    this.fetchDevices()
  },
  
  watch: {
    showAddDialog(val) {
      if (!val) {
        this.resetForm()
      }
    }
  }
}
</script>

<style scoped>
.device-management {
  background: white;
  border-radius: 8px;
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.page-header h2 {
  margin: 0;
  color: #333;
}

.search-bar {
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.device-list {
  margin-top: 20px;
}

.qr-container {
  text-align: center;
  padding: 20px;
}

.qr-code {
  display: flex;
  justify-content: center;
  margin-bottom: 15px;
}

.qr-info {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin: 10px 0 5px 0;
}

.qr-number {
  font-size: 14px;
  color: #666;
  margin: 0;
}

:deep(.el-dialog__body) {
  padding: 20px;
}

/* 送审日期在30天内的行背景色 */
:deep(.review-warning-row) {
  background-color: #fef0f0 !important;
}

:deep(.review-warning-row:hover) {
  background-color: #fde2e2 !important;
}
</style>